// LandscapingDashboard — single-page ops center for a landscaping / snow crew.
// Four tabs on one screen: Route Today · Snow Dispatch · Inventory · Seasonal
// Billing. Mirrors the pest/HVAC single-radar UX so the crew has one URL to
// remember.
import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import {
  TreeEvergreen, Snowflake, Package, CheckCircle, Plus, Trash, Sparkle,
  PaperPlaneTilt, Warning, Clock, MapPin, FilePdf, ArrowRight, X, Path,
  Buildings, CurrencyDollar, House,
} from "@phosphor-icons/react";

const dollars = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
import { todayLocalISO as todayIso } from "@/lib/dateHelpers";

const PROP_TYPES = [
  { key: "residential", label: "Residential" },
  { key: "commercial", label: "Commercial" },
  { key: "hoa", label: "HOA" },
  { key: "municipal", label: "Municipal" },
];
const SERVICES = [
  { key: "mow_and_blow", label: "Mow & Blow" },
  { key: "mow", label: "Mow only" },
  { key: "fert_squirt", label: "Fert" },
  { key: "spring_cleanup", label: "Spring cleanup" },
  { key: "fall_cleanup", label: "Fall cleanup" },
  { key: "snow_plow", label: "Snow plow" },
  { key: "salt_only", label: "Salt only" },
  { key: "shovel_walkway", label: "Shovel walk" },
];
const CADENCES = [
  { key: "weekly", label: "Weekly" },
  { key: "biweekly", label: "Biweekly" },
  { key: "monthly", label: "Monthly" },
];
const INV_ITEMS = [
  { key: "rock_salt", label: "Rock salt" },
  { key: "treated_salt", label: "Treated salt" },
  { key: "calcium_chloride", label: "Calcium chloride" },
  { key: "diesel", label: "Diesel" },
  { key: "gasoline", label: "Gasoline" },
  { key: "hardwood_mulch", label: "Hardwood mulch" },
  { key: "black_mulch", label: "Black mulch" },
  { key: "other", label: "Other" },
];

const RISK_META = {
  dispatch: { label: "DISPATCH", cls: "bg-cyan-500 text-white", icon: <Snowflake weight="fill" size={12}/> },
  clear:    { label: "CLEAR",    cls: "bg-emerald-500 text-white", icon: <CheckCircle weight="fill" size={12}/> },
  unknown:  { label: "UNKNOWN",  cls: "bg-slate-400 text-white",   icon: <Warning weight="fill" size={12}/> },
};

export default function LandscapingDashboard() {
  const [tab, setTab] = useState("route"); // route | snow | inventory | billing
  const [summary, setSummary] = useState(null);
  const [saltRisk, setSaltRisk] = useState(null);

  const reloadSummary = async () => {
    try {
      const [s, sr] = await Promise.all([
        api.get("/landscaping/summary"),
        api.get("/landscaping/summary/salt-risk"),
      ]);
      setSummary(s.data);
      setSaltRisk(sr.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { reloadSummary(); }, []);

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="landscaping-dashboard">
        {/* Salt Runout — big red banner when a storm is due AND salt is low */}
        {saltRisk?.runout_risk && (
          <div className="mb-4 rounded-xl border-4 border-red-500 bg-red-50 p-4 flex items-start gap-3 animate-pulse" data-testid="salt-runout-banner">
            <div className="h-10 w-10 rounded-lg bg-red-600 text-white flex items-center justify-center shrink-0">
              <Warning weight="fill" size={22}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-lg tracking-tight text-red-900" data-testid="salt-runout-title">
                SALT RUNOUT — Storm inside 48h + {saltRisk.salt_low_count} bin{saltRisk.salt_low_count === 1 ? "" : "s"} under low-stock line
              </div>
              <div className="text-xs text-red-800 font-medium mt-1">{saltRisk.snow_reason}</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(saltRisk.salt_low_bins || []).map(b => (
                  <span key={b.id} className="text-[10px] font-black uppercase tracking-widest bg-red-600 text-white px-2 py-0.5 rounded">
                    {b.item.replace(/_/g, " ")} · {b.label || "—"} · {b.on_hand}/{b.low_stock_at} {b.unit}
                  </span>
                ))}
              </div>
              <button type="button" onClick={() => setTab("inventory")}
                data-testid="salt-runout-jump-inventory"
                className="mt-3 h-9 px-3 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                Jump to Inventory · Refill Now <ArrowRight size={12}/>
              </button>
            </div>
          </div>
        )}

        {/* Hero */}
        <div className="flex items-center gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl bg-emerald-600/15 text-emerald-600 flex items-center justify-center">
            <TreeEvergreen weight="fill" size={22}/>
          </div>
          <div>
            <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight" data-testid="landscaping-heading">
              Landscaping & Snow — Ops Center
            </h1>
            <div className="text-xs sm:text-sm text-slate-500 font-medium">
              Route today, salt inventory, weather-triggered dispatch, and seasonal invoices — one screen.
            </div>
          </div>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <KpiTile label="Properties" value={summary?.properties ?? "—"} icon={<Buildings size={16}/>} testid="ls-tile-properties"/>
          <KpiTile label="Snow accounts" value={summary?.snow_accounts ?? "—"} icon={<Snowflake size={16}/>} testid="ls-tile-snow"/>
          <KpiTile label="Stops today"
                    value={`${summary?.stops_done_today ?? 0} / ${summary?.stops_today ?? 0}`}
                    icon={<Path size={16}/>} testid="ls-tile-stops"/>
          <KpiTile label="Low-stock bins" value={summary?.low_stock_count ?? "—"} icon={<Package size={16}/>}
                    accent={(summary?.low_stock_count || 0) > 0 ? "text-amber-500" : ""}
                    testid="ls-tile-low-stock"/>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1" data-testid="landscaping-tabs">
          <TabBtn active={tab === "route"} onClick={() => setTab("route")} testid="tab-route">
            <Path size={14}/> Route Today
          </TabBtn>
          <TabBtn active={tab === "snow"} onClick={() => setTab("snow")} testid="tab-snow">
            <Snowflake size={14}/> Snow Dispatch
          </TabBtn>
          <TabBtn active={tab === "inventory"} onClick={() => setTab("inventory")} testid="tab-inventory">
            <Package size={14}/> Inventory
          </TabBtn>
          <TabBtn active={tab === "billing"} onClick={() => setTab("billing")} testid="tab-billing">
            <CurrencyDollar size={14}/> Seasonal Billing
          </TabBtn>
          <div className="flex-1"/>
          <button type="button" onClick={reloadSummary}
                    data-testid="ls-refresh-summary"
                    className="h-9 px-3 rounded-lg text-[10px] font-black uppercase tracking-widest border-2 border-slate-200 hover:border-slate-900 bg-white">
            Refresh
          </button>
        </div>

        {tab === "route" && <RouteTab reloadSummary={reloadSummary}/>}
        {tab === "snow" && <SnowTab reloadSummary={reloadSummary}/>}
        {tab === "inventory" && <InventoryTab reloadSummary={reloadSummary}/>}
        {tab === "billing" && <BillingTab reloadSummary={reloadSummary}/>}
      </div>
    </AdminShell>
  );
}

function KpiTile({ label, value, icon, accent = "", testid }) {
  return (
    <div className="bg-white border-2 border-slate-200 rounded-xl p-3" data-testid={testid}>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">{icon} {label}</div>
      <div className={`font-display font-black text-xl sm:text-2xl mt-1 tracking-tight ${accent}`}>{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`h-9 px-3 rounded-lg text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 border-2 whitespace-nowrap transition ${active
        ? "bg-emerald-600 border-emerald-600 text-white"
        : "bg-white border-slate-200 hover:border-slate-900 text-slate-800"}`}>
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ROUTE TAB
// ═══════════════════════════════════════════════════════════════════════
function RouteTab({ reloadSummary }) {
  const [stops, setStops] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [properties, setProperties] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ property_id: "", stop_date: todayIso(), crew: "Crew A", service_type: "mow_and_blow" });
  const [optimizeOpen, setOptimizeOpen] = useState(false);
  const [optimizeLoading, setOptimizeLoading] = useState(false);
  const [optimizeStart, setOptimizeStart] = useState("");
  const [optimizeResult, setOptimizeResult] = useState(null);

  const load = async () => {
    try {
      const [stopsR, propsR] = await Promise.all([
        api.get("/landscaping/route-stops"),
        api.get("/landscaping/properties"),
      ]);
      setStops(stopsR.data.stops || []);
      setByStatus(stopsR.data.by_status || {});
      setProperties(propsR.data.properties || []);
    } catch { toast.error("Could not load route"); }
  };
  useEffect(() => { load(); }, []);

  const addStop = async () => {
    if (!form.property_id) { toast.error("Pick a property"); return; }
    try {
      await api.post("/landscaping/route-stops", form);
      toast.success("Stop added");
      setAddOpen(false);
      setForm({ property_id: "", stop_date: todayIso(), crew: "Crew A", service_type: "mow_and_blow" });
      await load(); await reloadSummary();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not add")); }
  };
  const setStatus = async (sid, status) => {
    try { await api.post(`/landscaping/route-stops/${sid}/status`, { status });
      toast.success("Updated"); await load(); await reloadSummary(); }
    catch { toast.error("Update failed"); }
  };
  const removeStop = async (sid) => {
    if (!window.confirm("Remove this stop?")) return;
    try { await api.delete(`/landscaping/route-stops/${sid}`); await load(); await reloadSummary(); }
    catch { toast.error("Delete failed"); }
  };
  const runOptimize = async () => {
    setOptimizeLoading(true); setOptimizeResult(null);
    try {
      const r = await api.post("/landscaping/route-optimize", {
        stop_date: todayIso(),
        start_address: optimizeStart || null,
      });
      setOptimizeResult(r.data);
      if ((r.data.ordered || []).length) toast.success(`Sequenced ${r.data.ordered.length} stops`);
      else toast.info(r.data.notes || "Nothing to optimize");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Optimize failed")); }
    finally { setOptimizeLoading(false); }
  };

  return (
    <div data-testid="landscaping-tab-route">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button type="button" onClick={() => setAddOpen(true)}
          data-testid="ls-add-stop-btn"
          className="h-10 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
          <Plus weight="bold" size={14}/> Add Stop
        </button>
        <button type="button" onClick={() => setOptimizeOpen(true)}
          data-testid="ls-optimize-btn"
          className="h-10 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
          <Sparkle weight="fill" size={14}/> AI Optimize
        </button>
        <div className="text-xs text-slate-500 font-medium ml-2">
          {byStatus.done || 0} done · {byStatus.en_route || 0} en route · {byStatus.scheduled || 0} scheduled
        </div>
      </div>

      {stops.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-300 p-8 text-center text-sm text-slate-500 font-medium" data-testid="ls-empty-stops">
          No stops on today's route yet. Add a property or run the optimizer.
        </div>
      ) : (
        <div className="bg-white border-2 border-slate-200 rounded-xl overflow-hidden">
          {stops.map((s, i) => (
            <div key={s.id} className={`p-3 flex items-start gap-3 ${i > 0 ? "border-t border-slate-100" : ""}`} data-testid={`ls-stop-${s.id}`}>
              <div className="h-8 w-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center font-black text-sm shrink-0">
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-sm truncate">{s.property_name}</div>
                <div className="text-[11px] text-slate-500 truncate">{s.property_address}</div>
                <div className="mt-1 flex items-center gap-1 flex-wrap">
                  <span className="text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{s.crew}</span>
                  <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{s.service_type.replace(/_/g," ")}</span>
                  <StatusChip status={s.status}/>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {s.status !== "done" && (
                  <button type="button" onClick={() => setStatus(s.id, "done")}
                    data-testid={`ls-stop-done-${s.id}`}
                    className="h-8 px-2 rounded-md bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                    <CheckCircle weight="fill" size={12}/> Done
                  </button>
                )}
                {s.status === "scheduled" && (
                  <button type="button" onClick={() => setStatus(s.id, "en_route")}
                    data-testid={`ls-stop-enroute-${s.id}`}
                    className="h-8 px-2 rounded-md bg-cyan-500 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                    <Clock weight="fill" size={12}/> En route
                  </button>
                )}
                <button type="button" onClick={() => removeStop(s.id)}
                  data-testid={`ls-stop-remove-${s.id}`}
                  className="h-8 w-8 rounded-md bg-white border-2 border-slate-200 hover:border-red-500 text-slate-500 hover:text-red-500 inline-flex items-center justify-center">
                  <Trash size={12}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add stop modal */}
      {addOpen && (
        <Modal onClose={() => setAddOpen(false)} title="Add Stop" testid="ls-add-stop-modal">
          <div className="space-y-3">
            <Field label="Property">
              <select value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                data-testid="ls-add-stop-property"
                className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium">
                <option value="">Pick a property…</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name} — {p.address}</option>)}
              </select>
              {properties.length === 0 && (
                <div className="text-[10px] text-amber-600 font-black uppercase tracking-widest mt-1">
                  No properties yet. Add one via the Route tab bulk-add below or seed the demo.
                </div>
              )}
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date">
                <input type="date" value={form.stop_date} onChange={(e) => setForm({ ...form, stop_date: e.target.value })}
                  data-testid="ls-add-stop-date"
                  className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
              </Field>
              <Field label="Crew">
                <input type="text" value={form.crew} onChange={(e) => setForm({ ...form, crew: e.target.value })}
                  data-testid="ls-add-stop-crew"
                  className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
              </Field>
            </div>
            <Field label="Service">
              <select value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                data-testid="ls-add-stop-service"
                className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium">
                {SERVICES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </Field>
            <button type="button" onClick={addStop}
              data-testid="ls-add-stop-save"
              className="w-full h-11 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest">
              Add stop
            </button>
          </div>
        </Modal>
      )}

      {/* Optimize modal */}
      {optimizeOpen && (
        <Modal onClose={() => setOptimizeOpen(false)} title="AI Route Optimizer" testid="ls-optimize-modal">
          <div className="space-y-3">
            <Field label="Start address (yard/truck home)">
              <input type="text" placeholder="e.g. 500 Yard Rd, Philadelphia PA"
                value={optimizeStart} onChange={(e) => setOptimizeStart(e.target.value)}
                data-testid="ls-optimize-start"
                className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
            </Field>
            <button type="button" onClick={runOptimize} disabled={optimizeLoading}
              data-testid="ls-optimize-run"
              className="w-full h-11 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest disabled:opacity-60 inline-flex items-center justify-center gap-2">
              <Sparkle weight="fill" size={14}/> {optimizeLoading ? "Sequencing…" : "Optimize with Claude Haiku"}
            </button>
            {optimizeResult && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 space-y-1" data-testid="ls-optimize-result">
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Sequenced {optimizeResult.ordered?.length || 0} stops</div>
                <div className="text-[11px] text-slate-700 leading-snug">{optimizeResult.notes}</div>
                {(optimizeResult.ordered || []).map((o, i) => (
                  <div key={o.id || i} className="text-[11px] text-slate-700">
                    <span className="font-black">{i + 1}.</span> {o.property} <span className="text-slate-400">— {o.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

function StatusChip({ status }) {
  const m = {
    scheduled: { label: "SCHEDULED", cls: "bg-slate-200 text-slate-700" },
    en_route: { label: "EN ROUTE",  cls: "bg-cyan-100 text-cyan-800" },
    done:      { label: "DONE",     cls: "bg-emerald-100 text-emerald-800" },
    skipped:   { label: "SKIPPED",  cls: "bg-amber-100 text-amber-800" },
  }[status] || { label: status.toUpperCase(), cls: "bg-slate-100 text-slate-600" };
  return <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
}

// ═══════════════════════════════════════════════════════════════════════
// SNOW TAB
// ═══════════════════════════════════════════════════════════════════════
function SnowTab({ reloadSummary }) {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [yard, setYard] = useState("");

  const loadLatest = async () => {
    try { const r = await api.get("/landscaping/snow/latest"); setScan(r.data.scan); }
    catch { /* ignore */ }
  };
  useEffect(() => { loadLatest(); }, []);

  const runScan = async () => {
    setLoading(true);
    try {
      const r = await api.post("/landscaping/snow/scan", { yard_address: yard || null });
      setScan({ ...r.data, scanned_at: r.data.scanned_at });
      toast.success(`Scan complete — ${r.data.risk}`);
      if (reloadSummary) await reloadSummary();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Scan failed")); }
    finally { setLoading(false); }
  };

  const meta = RISK_META[scan?.risk] || RISK_META.unknown;

  return (
    <div data-testid="landscaping-tab-snow">
      <div className="bg-white border-2 border-slate-200 rounded-xl p-4 mb-3">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <input list="known-addresses" autoComplete="off" type="text" placeholder="Yard address (optional — forecast anchor)"
            value={yard} onChange={(e) => setYard(e.target.value)}
            data-testid="ls-snow-yard"
            className="flex-1 min-w-[220px] h-10 px-3 border-2 border-slate-200 rounded-lg font-medium text-sm"/>
          <button type="button" onClick={runScan} disabled={loading}
            data-testid="ls-snow-scan-btn"
            className="h-10 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-60">
            <Snowflake weight="fill" size={14}/> {loading ? "Scanning…" : "Scan NWS Forecast"}
          </button>
        </div>
        {scan ? (
          <div className="space-y-3" data-testid="ls-snow-scan">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded inline-flex items-center gap-1 ${meta.cls}`}
                    data-testid="ls-snow-risk">
                {meta.icon} {meta.label}
              </span>
              <span className="text-xs text-slate-700 font-medium">{scan.reason}</span>
              {scan.scanned_at && (
                <span className="text-[10px] text-slate-400 ml-auto">Scanned {new Date(scan.scanned_at).toLocaleString()}</span>
              )}
            </div>
            {scan.draft_sms && (
              <div className="rounded-lg bg-cyan-50 border border-cyan-200 p-3">
                <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700 mb-1">Draft crew SMS (nothing sent)</div>
                <div className="text-sm text-slate-800 leading-snug italic" data-testid="ls-snow-draft">"{scan.draft_sms}"</div>
                <div className="mt-2 text-[10px] text-slate-500 font-medium">Copy this into your crew Slack / group text.</div>
              </div>
            )}
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Sites on dispatch board ({(scan.sites || []).length})</div>
              <div className="border-2 border-slate-200 rounded-lg divide-y divide-slate-100">
                {(scan.sites || []).map(s => (
                  <div key={s.id} className="p-3 flex items-center gap-2" data-testid={`ls-snow-site-${s.id}`}>
                    <House size={16} className="text-slate-400 shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-sm truncate">{s.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{s.address}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      {(s.service_mix || []).map(x => (
                        <span key={x} className="text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{x.replace(/_/g," ")}</span>
                      ))}
                    </div>
                  </div>
                ))}
                {(scan.sites || []).length === 0 && (
                  <div className="p-6 text-center text-sm text-slate-500 font-medium">
                    No properties tagged for snow service yet. Turn on the snow flag on a property.
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-500 font-medium" data-testid="ls-snow-empty">
            No scan yet. Tap "Scan NWS Forecast" — the free National Weather Service API is checked for the next 48h at your yard's location.
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// INVENTORY TAB
// ═══════════════════════════════════════════════════════════════════════
function InventoryTab({ reloadSummary }) {
  const [items, setItems] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ item: "rock_salt", label: "", unit: "tons", on_hand: 0, low_stock_at: 1, location: "" });
  const [adjustFor, setAdjustFor] = useState(null);
  const [adjustDelta, setAdjustDelta] = useState("");
  const [adjustNote, setAdjustNote] = useState("");

  const load = async () => {
    try { const r = await api.get("/landscaping/inventory"); setItems(r.data.items || []); }
    catch { toast.error("Could not load inventory"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.item) { toast.error("Pick an item"); return; }
    try {
      await api.post("/landscaping/inventory", {
        ...form,
        on_hand: Number(form.on_hand) || 0,
        low_stock_at: Number(form.low_stock_at) || 0,
      });
      toast.success("Bin added");
      setAddOpen(false);
      setForm({ item: "rock_salt", label: "", unit: "tons", on_hand: 0, low_stock_at: 1, location: "" });
      await load(); await reloadSummary();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
  };
  const adjust = async () => {
    if (!adjustFor) return;
    const n = Number(adjustDelta);
    if (!n || Number.isNaN(n)) { toast.error("Enter a positive or negative number"); return; }
    try {
      await api.post(`/landscaping/inventory/${adjustFor.id}/adjust`, { delta: n, note: adjustNote });
      toast.success("Adjusted");
      setAdjustFor(null); setAdjustDelta(""); setAdjustNote("");
      await load(); await reloadSummary();
    } catch { toast.error("Adjust failed"); }
  };
  const remove = async (iid) => {
    if (!window.confirm("Delete this bin?")) return;
    try { await api.delete(`/landscaping/inventory/${iid}`); await load(); await reloadSummary(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="landscaping-tab-inventory">
      <div className="flex items-center gap-2 mb-3">
        <button type="button" onClick={() => setAddOpen(true)}
          data-testid="ls-add-bin-btn"
          className="h-10 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
          <Plus weight="bold" size={14}/> Add Bin
        </button>
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-300 p-8 text-center text-sm text-slate-500 font-medium" data-testid="ls-empty-inventory">
          No inventory yet. Add salt bins, fuel tanks, mulch stockpiles.
        </div>
      ) : (
        <div className="bg-white border-2 border-slate-200 rounded-xl overflow-hidden">
          {items.map((i, idx) => {
            const itemMeta = INV_ITEMS.find(x => x.key === i.item) || { label: i.item };
            return (
              <div key={i.id} className={`p-3 flex items-center gap-3 ${idx > 0 ? "border-t border-slate-100" : ""}`} data-testid={`ls-bin-${i.id}`}>
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${i.is_low ? "bg-amber-100 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>
                  <Package weight="fill" size={16}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-sm truncate">
                    {itemMeta.label} <span className="text-slate-400 font-medium">· {i.label || i.location || "—"}</span>
                  </div>
                  <div className="text-[11px] text-slate-500">
                    <span className="font-mono">{i.on_hand} / {i.low_stock_at} {i.unit}</span>
                    {i.is_low && <span className="ml-2 text-[9px] font-black uppercase tracking-widest text-amber-600">LOW</span>}
                  </div>
                </div>
                <button type="button" onClick={() => setAdjustFor(i)}
                  data-testid={`ls-bin-adjust-${i.id}`}
                  className="h-8 px-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-black uppercase tracking-widest">
                  Adjust
                </button>
                <button type="button" onClick={() => remove(i.id)}
                  data-testid={`ls-bin-remove-${i.id}`}
                  className="h-8 w-8 rounded-md bg-white border-2 border-slate-200 hover:border-red-500 text-slate-500 hover:text-red-500 inline-flex items-center justify-center">
                  <Trash size={12}/>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {addOpen && (
        <Modal onClose={() => setAddOpen(false)} title="Add Bin" testid="ls-add-bin-modal">
          <div className="space-y-3">
            <Field label="Item">
              <select value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })}
                data-testid="ls-add-bin-item"
                className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium">
                {INV_ITEMS.map(i => <option key={i.key} value={i.key}>{i.label}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Label (optional)">
                <input type="text" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                  data-testid="ls-add-bin-label"
                  className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
              </Field>
              <Field label="Location">
                <input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                  data-testid="ls-add-bin-location"
                  className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Unit">
                <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  data-testid="ls-add-bin-unit"
                  className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium">
                  <option value="tons">tons</option>
                  <option value="gal">gal</option>
                  <option value="yd3">yd³</option>
                  <option value="bags">bags</option>
                </select>
              </Field>
              <Field label="On hand">
                <input type="number" step="0.1" value={form.on_hand} onChange={(e) => setForm({ ...form, on_hand: e.target.value })}
                  data-testid="ls-add-bin-onhand"
                  className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
              </Field>
              <Field label="Low at">
                <input type="number" step="0.1" value={form.low_stock_at} onChange={(e) => setForm({ ...form, low_stock_at: e.target.value })}
                  data-testid="ls-add-bin-lowat"
                  className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
              </Field>
            </div>
            <button type="button" onClick={save}
              data-testid="ls-add-bin-save"
              className="w-full h-11 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest">
              Save bin
            </button>
          </div>
        </Modal>
      )}

      {adjustFor && (
        <Modal onClose={() => setAdjustFor(null)} title={`Adjust ${adjustFor.item.replace(/_/g," ")}`} testid="ls-adjust-modal">
          <div className="space-y-3">
            <div className="text-[11px] text-slate-500 font-medium">
              Currently <span className="font-mono">{adjustFor.on_hand} {adjustFor.unit}</span> — enter positive to add, negative to consume.
            </div>
            <Field label="Delta">
              <input type="number" step="0.1" value={adjustDelta} onChange={(e) => setAdjustDelta(e.target.value)}
                data-testid="ls-adjust-delta"
                placeholder="e.g. -0.5"
                className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
            </Field>
            <Field label="Note">
              <input type="text" value={adjustNote} onChange={(e) => setAdjustNote(e.target.value)}
                data-testid="ls-adjust-note"
                placeholder="e.g. Used for Chestnut HOA salt run"
                className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
            </Field>
            <button type="button" onClick={adjust}
              data-testid="ls-adjust-save"
              className="w-full h-11 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest">
              Apply adjustment
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// BILLING TAB
// ═══════════════════════════════════════════════════════════════════════
function BillingTab({ reloadSummary }) {
  const [templates, setTemplates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [totalCents, setTotalCents] = useState(0);
  const [properties, setProperties] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ property_id: "", template: "spring_cleanup", amount_cents: "", memo: "", due_date: "" });

  const load = async () => {
    try {
      const [t, i, p] = await Promise.all([
        api.get("/landscaping/billing/templates"),
        api.get("/landscaping/invoices"),
        api.get("/landscaping/properties"),
      ]);
      setTemplates(t.data.templates || []);
      setInvoices(i.data.invoices || []);
      setTotalCents(i.data.total_cents || 0);
      setProperties(p.data.properties || []);
    } catch { toast.error("Could not load invoices"); }
  };
  useEffect(() => { load(); }, []);

  const templateMeta = useMemo(() => Object.fromEntries(templates.map(t => [t.key, t])), [templates]);
  const currentTpl = templateMeta[form.template];

  const save = async () => {
    if (!form.property_id) { toast.error("Pick a property"); return; }
    try {
      const body = {
        property_id: form.property_id,
        template: form.template,
        memo: form.memo,
        due_date: form.due_date || null,
      };
      if (form.amount_cents !== "") body.amount_cents = Math.round(Number(form.amount_cents));
      await api.post("/landscaping/invoices", body);
      toast.success("Invoice drafted");
      setAddOpen(false);
      setForm({ property_id: "", template: "spring_cleanup", amount_cents: "", memo: "", due_date: "" });
      await load(); await reloadSummary();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
  };
  const setStatus = async (iid, status) => {
    try { await api.post(`/landscaping/invoices/${iid}/status`, { status });
      toast.success("Updated"); await load(); await reloadSummary(); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (iid) => {
    if (!window.confirm("Delete this invoice?")) return;
    try { await api.delete(`/landscaping/invoices/${iid}`); await load(); await reloadSummary(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="landscaping-tab-billing">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button type="button" onClick={() => setAddOpen(true)}
          data-testid="ls-add-invoice-btn"
          className="h-10 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
          <Plus weight="bold" size={14}/> New Invoice
        </button>
        <div className="text-xs text-slate-500 font-medium ml-2">
          {invoices.length} invoices · {dollars(totalCents)} total
        </div>
      </div>
      {invoices.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-300 p-8 text-center text-sm text-slate-500 font-medium" data-testid="ls-empty-invoices">
          No invoices yet. Draft one from a template — spring cleanup, mid-season fert, snow pre-pay.
        </div>
      ) : (
        <div className="bg-white border-2 border-slate-200 rounded-xl overflow-hidden">
          {invoices.map((inv, i) => (
            <div key={inv.id} className={`p-3 flex items-center gap-3 ${i > 0 ? "border-t border-slate-100" : ""}`} data-testid={`ls-invoice-${inv.id}`}>
              <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <CurrencyDollar weight="fill" size={16}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-sm truncate">{inv.label} — {dollars(inv.amount_cents)}</div>
                <div className="text-[11px] text-slate-500 truncate">{inv.property_name} · {inv.due_date ? `Due ${inv.due_date}` : "No due date"}</div>
                {inv.memo && <div className="text-[11px] text-slate-400 italic truncate">{inv.memo}</div>}
              </div>
              <InvoiceStatusChip status={inv.status}/>
              <div className="flex items-center gap-1">
                {inv.status === "draft" && (
                  <button type="button" onClick={() => setStatus(inv.id, "sent")}
                    data-testid={`ls-invoice-send-${inv.id}`}
                    className="h-8 px-2 rounded-md bg-cyan-500 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                    <PaperPlaneTilt weight="fill" size={12}/> Sent
                  </button>
                )}
                {inv.status === "sent" && (
                  <button type="button" onClick={() => setStatus(inv.id, "paid")}
                    data-testid={`ls-invoice-paid-${inv.id}`}
                    className="h-8 px-2 rounded-md bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                    <CheckCircle weight="fill" size={12}/> Paid
                  </button>
                )}
                <button type="button" onClick={() => remove(inv.id)}
                  data-testid={`ls-invoice-remove-${inv.id}`}
                  className="h-8 w-8 rounded-md bg-white border-2 border-slate-200 hover:border-red-500 text-slate-500 hover:text-red-500 inline-flex items-center justify-center">
                  <Trash size={12}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {addOpen && (
        <Modal onClose={() => setAddOpen(false)} title="Draft Invoice from Template" testid="ls-invoice-modal">
          <div className="space-y-3">
            <Field label="Property">
              <select value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                data-testid="ls-invoice-property"
                className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium">
                <option value="">Pick a property…</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Template">
              <select value={form.template} onChange={(e) => setForm({ ...form, template: e.target.value, amount_cents: "" })}
                data-testid="ls-invoice-template"
                className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium">
                {templates.map(t => <option key={t.key} value={t.key}>{t.label} — {dollars(t.default_cents)}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={`Amount ($) — default ${currentTpl ? dollars(currentTpl.default_cents) : "—"}`}>
                <input type="number" step="1"
                  placeholder={currentTpl ? `${(currentTpl.default_cents / 100).toFixed(0)}` : ""}
                  value={form.amount_cents === "" ? "" : (Number(form.amount_cents) / 100).toString()}
                  onChange={(e) => setForm({ ...form, amount_cents: e.target.value === "" ? "" : Math.round(Number(e.target.value) * 100) })}
                  data-testid="ls-invoice-amount"
                  className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
              </Field>
              <Field label="Due date">
                <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  data-testid="ls-invoice-due"
                  className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
              </Field>
            </div>
            <Field label="Memo (optional)">
              <input type="text" value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })}
                data-testid="ls-invoice-memo"
                placeholder="e.g. Includes leaf removal + mulch beds"
                className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
            </Field>
            <button type="button" onClick={save}
              data-testid="ls-invoice-save"
              className="w-full h-11 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest">
              Draft invoice
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function InvoiceStatusChip({ status }) {
  const m = {
    draft: { label: "DRAFT", cls: "bg-slate-200 text-slate-700" },
    sent:  { label: "SENT",  cls: "bg-cyan-100 text-cyan-800" },
    paid:  { label: "PAID",  cls: "bg-emerald-100 text-emerald-800" },
    void:  { label: "VOID",  cls: "bg-red-100 text-red-800" },
  }[status] || { label: (status || "").toUpperCase(), cls: "bg-slate-100 text-slate-600" };
  return <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
}

// ═══════════════════════════════════════════════════════════════════════
// Shared modal + field
// ═══════════════════════════════════════════════════════════════════════
function Modal({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="font-display font-black text-lg tracking-tight">{title}</div>
          <button type="button" onClick={onClose} className="h-8 w-8 rounded-md hover:bg-slate-100 inline-flex items-center justify-center" data-testid="modal-close">
            <X size={16}/>
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
